import { TextInput, Badge, Flex, Text, Checkbox, Select } from '@mantine/core';
import { EOB } from 'interfaces';
import { MRT_ColumnDef } from 'mantine-react-table';
import { useState } from 'react';
import { IconAlertCircle } from '@tabler/icons-react';
import { provinceOptions } from 'constants/provinceConstants';
import { useTranslation } from '@refinedev/core';
import type { Province } from '@billing/validation/payor';

const isProvince = (value: string | null | undefined): value is Province =>
  provinceOptions.includes(value ?? '');

export const EOBColumns = (defaultProvince?: string): MRT_ColumnDef<EOB>[] => {
  const { translate } = useTranslation();
  const columns: MRT_ColumnDef<EOB>[] = [
    {
      accessorKey: 'code',
      header: 'Code',
      maxSize: 80,
      minSize: 40,
      size: 50,
      Cell: ({ cell }) => {
        const value = cell.getValue() as string;
        return <Badge size="lg">{value}</Badge>;
      },
      Edit: ({ cell }) => {
        const [value, setValue] = useState(cell.getValue() as string);
        return (
          <TextInput
            value={value}
            label="EOB Code"
            onChange={(event) => {
              cell.row._valuesCache.code = event.currentTarget.value;
              setValue(event.currentTarget.value);
            }}
          />
        );
      },
    },
    {
      accessorKey: 'description',
      header: 'Description',
      minSize: 180,
      maxSize: 1000,
      size: 650,

      Cell: ({ cell }) => {
        const value = cell.getValue() as string;
        return <Text size="xs">{value}</Text>;
      },
      Edit: ({ cell }) => {
        const [value, setValue] = useState(cell.getValue() as string);
        return (
          <TextInput
            value={value}
            label="EOB Description"
            onChange={(event) => {
              cell.row._valuesCache.description = event.currentTarget.value;
              setValue(event.currentTarget.value);
            }}
          />
        );
      },
    },
    {
      accessorKey: 'province',
      header: translate('pages:eobs.labels.province', 'Province'),
      size: 80,
      Edit: ({ cell }) => {
        const province = cell.getValue<Province | null>();
        const [value, setValue] = useState<Province | null>(
          province ?? (isProvince(defaultProvince) ? defaultProvince : null)
        );
        return (
          <Select
            required
            value={value}
            data={provinceOptions}
            label={translate('pages:eobs.labels.province', 'Province')}
            onChange={(selectedProvince: string | null) => {
              const provinceValue: Province | null = isProvince(
                selectedProvince
              )
                ? selectedProvince
                : null;
              cell.row._valuesCache.province = provinceValue;
              setValue(provinceValue);
            }}
          />
        );
      },
    },
    {
      accessorKey: 'actionType',
      header: 'Action Code',
      size: 15,
      Cell: ({ cell }) => {
        const value = cell.getValue() as string;
        return (
          <Flex justify={'center'} align={'center'}>
            {!value ? (
              <Badge color="red" size="xl" />
            ) : (
              <Badge color="blue" size="xl">
                {value}
              </Badge>
            )}
          </Flex>
        );
      },
      Edit: ({ cell }) => {
        const [value, setValue] = useState(
          (cell.getValue() as string | null) ?? ''
        );
        return (
          <TextInput
            value={value}
            label="EOB Action"
            onChange={(event) => {
              cell.row._valuesCache.actionType = event.currentTarget.value;
              setValue(event.currentTarget.value);
            }}
          />
        );
      },
    },
    {
      accessorKey: 'actionDescription',
      header: 'Action Description',
      minSize: 180,
      maxSize: 800,
      size: 650,
      Cell: ({ cell }) => {
        const value = cell.getValue() as string;
        return <Text size="xs">{value}</Text>;
      },
    },
    {
      accessorKey: 'reviewRequired',
      header: 'Review',
      minSize: 30,
      Cell: ({ cell }) => {
        const value = cell.getValue() as boolean;
        return value ? <IconAlertCircle size={25} /> : <></>;
      },
      Edit: ({ row }) => {
        return row._valuesCache.reviewRequired ? (
          <Flex
            justify={'center'}
            align={'center'}
            direction={'column'}
            gap={'lg'}
          >
            <Text>Mark As Reviewed</Text>
            <Checkbox
              onChange={() => {
                row._valuesCache.reviewRequired = false;
              }}
            />
          </Flex>
        ) : (
          <> </>
        );
      },
    },
  ];
  return columns;
};
